"use client";

import { X, Image as ImageIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import Image from "next/image";
import Slider from "react-slick";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const CustomPrevArrow = (props: any) => (
  <button
    onClick={props.onClick}
    className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 z-20 p-2.5 bg-transparent border border-white/40 hover:border-white rounded-full transition-all text-white/70 hover:text-white"
    aria-label="Previous slide"
  >
    <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
  </button>
);

const CustomNextArrow = (props: any) => (
  <button
    onClick={props.onClick}
    className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 z-20 p-2.5 bg-transparent border border-white/40 hover:border-white rounded-full transition-all text-white/70 hover:text-white"
    aria-label="Next slide"
  >
    <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
  </button>
);

const CardPrevArrow = (props: any) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      props.onClick();
    }}
    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-1.5 bg-black/30 hover:bg-black/50 border border-white/20 rounded-full transition-opacity opacity-0 group-hover:opacity-100 backdrop-blur-sm shadow-sm"
    aria-label="Previous slide"
  >
    <ChevronLeft className="w-4 h-4 text-white" />
  </button>
);

const CardNextArrow = (props: any) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      props.onClick();
    }}
    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-1.5 bg-black/30 hover:bg-black/50 border border-white/20 rounded-full transition-opacity opacity-0 group-hover:opacity-100 backdrop-blur-sm shadow-sm"
    aria-label="Next slide"
  >
    <ChevronRight className="w-4 h-4 text-white" />
  </button>
);

export default function TicketImageSlider({
  images,
  fallbackImage,
}: {
  images: any[];
  fallbackImage?: string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const displayImages =
    images && images.length > 0
      ? images
      : fallbackImage
        ? [{ Image: fallbackImage }]
        : [];
  const [nav1, setNav1] = useState<Slider | null>(null);
  const [nav2, setNav2] = useState<Slider | null>(null);
  const slider1 = useRef<Slider>(null);
  const slider2 = useRef<Slider>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") setIsModalOpen(false);
      };
      window.addEventListener("keydown", handleKeyDown);

      const timer = setTimeout(() => {
        setNav1(slider1.current);
        setNav2(slider2.current);
        window.dispatchEvent(new Event("resize"));
      }, 100);

      return () => {
        clearTimeout(timer);
        document.body.style.overflow = "unset";
        window.removeEventListener("keydown", handleKeyDown);
        setNav1(null);
        setNav2(null);
      };
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isModalOpen]);

  if (displayImages.length === 0) return null;

  const cardSettings = {
    dots: false,
    infinite: displayImages.length > 1,
    speed: 500,
    fade: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    // autoplay: displayImages.length > 1,
    autoplaySpeed: 3000,
    arrows: displayImages.length > 1,
    prevArrow: <CardPrevArrow />,
    nextArrow: <CardNextArrow />,
  };

  return (
    <>
      <div
        className="relative group rounded-lg overflow-hidden aspect-square mb-3 cursor-pointer shadow-inner bg-gray-100 border border-gray-100"
        onClick={() => setIsModalOpen(true)}
        aria-label="View gallery"
      >
        <Slider {...cardSettings}>
          {displayImages.map((img, idx) => (
            <div
              key={idx}
              className="relative aspect-square w-full outline-none"
            >
              <Image
                src={img.Image}
                alt={`Ticket Image ${idx + 1}`}
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, 33vw"
              />
            </div>
          ))}
        </Slider>
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none duration-300">
          <div className="bg-white/20 backdrop-blur-md p-3 rounded-full scale-75 group-hover:scale-100 transition-transform duration-300">
            <ImageIcon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>

      {mounted && typeof window !== "undefined"
        ? createPortal(
            <AnimatePresence>
              {isModalOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[9999999] bg-black/90 backdrop-blur-xl flex flex-col overflow-hidden m-0"
                >
                  {/* Top Bar Menu */}
                  <div className="flex justify-between items-center px-4 sm:px-8 py-4 sm:py-6 text-white z-10 relative bg-gradient-to-b from-black/80 via-black/30 to-transparent">
                    <span className="text-xl md:text-2xl font-bold tracking-tight text-white border-l-4 border-orange-500 pl-4">
                      Gallery View
                    </span>
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="bg-white/10 hover:bg-orange-600 p-2.5 sm:p-3 rounded-full backdrop-blur-lg transition-all group border border-white/10 flex-shrink-0"
                      aria-label="Close gallery"
                    >
                      <X className="w-5 h-5 sm:w-6 sm:h-6 text-white group-hover:rotate-90 transition-transform duration-500" />
                    </button>
                  </div>

                  {/* Swiper-like synced Slider Container */}
                  <div className="flex-1 flex flex-col justify-center items-center py-4 px-4 md:px-24 w-full mx-auto relative">
                    {/* Main Large Slider */}
                    <div className="w-full relative px-2 md:px-12">
                      <Slider
                        asNavFor={nav2 as any}
                        ref={slider1}
                        fade={true}
                        arrows={true}
                        prevArrow={<CustomPrevArrow />}
                        nextArrow={<CustomNextArrow />}
                        className="main-gallery-slider"
                      >
                        {displayImages.map((img, idx) => (
                          <div
                            key={idx}
                            className="relative h-[55vh] md:h-[65vh] outline-none"
                          >
                            <Image
                              src={img.Image}
                              alt={`Gallery Image ${idx + 1}`}
                              fill
                              className="object-contain"
                              priority
                              sizes="100vw"
                            />
                          </div>
                        ))}
                      </Slider>
                    </div>

                    {/* Sync Thumbnail Slider */}
                    <div className="w-full max-w-2xl">
                      <Slider
                        asNavFor={nav1 as any}
                        ref={slider2}
                        slidesToShow={Math.min(displayImages.length, 6)}
                        slidesToScroll={1}
                        infinite={false}
                        swipeToSlide={true}
                        focusOnSelect={true}
                        centerMode={displayImages.length > 6}
                        centerPadding="0"
                        arrows={false}
                        className="thumbnail-slider"
                        responsive={[
                          {
                            breakpoint: 1024,
                            settings: {
                              slidesToShow: Math.min(displayImages.length, 5),
                              slidesToScroll: 1,
                              centerMode: displayImages.length > 5,
                            },
                          },
                          {
                            breakpoint: 768,
                            settings: {
                              slidesToShow: Math.min(displayImages.length, 4),
                              slidesToScroll: 1,
                              centerMode: displayImages.length > 4,
                            },
                          },
                          {
                            breakpoint: 480,
                            settings: {
                              slidesToShow: Math.min(displayImages.length, 2),
                              slidesToScroll: 1,
                              centerMode: displayImages.length > 3,
                            },
                          },
                        ]}
                      >
                        {displayImages.map((img, idx) => (
                          <div key={idx} className="px-2 outline-none py-2">
                            <div className="relative h-[65px] md:h-[80px] aspect-video rounded-xl overflow-hidden cursor-pointer border-3 border-transparent transition-all duration-300 [.slick-current_&]:border-[#EAB308] group mx-auto">
                              <Image
                                src={img.Image}
                                alt={`Thumb ${idx}`}
                                fill
                                className="object-cover group-hover:scale-110 transition-transform duration-700"
                                sizes="200px"
                              />
                            </div>
                          </div>
                        ))}
                      </Slider>
                    </div>
                  </div>

                  {/* Custom Styles for active thumbnail sync if needed */}
                  <style jsx global>{`
                    .thumbnail-slider .slick-track {
                      display: flex !important;
                      align-items: center !important;
                      padding: 20px 0;
                    }
                    .main-gallery-slider .slick-prev,
                    .main-gallery-slider .slick-next {
                      width: 40px;
                      height: 40px;
                    }
                  `}</style>
                </motion.div>
              )}
            </AnimatePresence>,
            document.body,
          )
        : null}
    </>
  );
}
